/**
 * leadUtils.js
 *
 * Helpers de leads compartilhados entre a tela de Leads e o chat do WhatsApp.
 */

import { normalizePhone, formatPhone } from './phoneUtils';

/** Só os dígitos do telefone, como o servidor grava o lead. */
export const onlyDigits = (rawPhone) => String(rawPhone || '').replace(/\D/g, '');

/**
 * Telefone do lead completo: DDD + número (10 dígitos ou mais, até 15 com DDI).
 * Mesma regra de set_lead.php / update_lead.php.
 */
export const isLeadPhoneComplete = (rawPhone) => {
  const length = onlyDigits(rawPhone).length;
  return length >= 10 && length <= 15;
};

/**
 * Telefone do lead no formato de leitura: "+55 (11) 98765-4321".
 * Lead cadastrado à mão guarda só DDD + número; `countryCode` é o DDI que o envio de
 * WhatsApp usa para ele (vazio quando o número já chega internacional).
 */
export function formatLeadPhone(rawPhone, countryCode) {
  const full = normalizePhone(rawPhone, countryCode);
  if (!full) return '';
  // formatPhone devolve o número como veio quando não tem 10 ou 11 dígitos
  if (full.startsWith('55')) return `+55 ${formatPhone(full.substring(2))}`;
  return `+${full}`;
}

/**
 * Separa um telefone cru (normalmente DDI + DDD + número) em DDI e telefone.
 * Mantém o padrão brasileiro como fallback, igual ao restante do produto.
 *
 * @param {string} rawPhone telefone em qualquer formatação
 * @returns {{ countryCode: string, phone: string }}
 */
export function splitPhoneIntoCountryCode(rawPhone) {
  const digits = String(rawPhone || '').replace(/\D/g, '');

  if (digits.startsWith('55') && digits.length >= 12) {
    return { countryCode: '55', phone: digits.substring(2) };
  }

  return { countryCode: '55', phone: digits };
}

/**
 * Monta o objeto de pré-preenchimento do CreatePatientPopup a partir de um lead.
 * Sem `id`, o popup segue tratando como criação de paciente.
 *
 * @param {object|null} lead lead (tela de Leads ou contexto do chat)
 * @param {string} phone telefone da conversa, quando o lead não tiver um
 * @returns {object} Data para o CreatePatientPopup
 */
export function buildPatientPrefillFromLead(lead, phone) {
  const { countryCode, phone: localPhone } = splitPhoneIntoCountryCode(lead?.phone || phone);

  return {
    status: 1,
    name: lead?.name || '',
    countryCode: countryCode,
    phone: localPhone,
    origin_channel_id: lead?.sourceId || '',
    origin_campaign_id: lead?.campaignId || '',
    leadId: lead?.id || null,
  };
}
